import { StringHelper } from '@mpt-fo/common/core/helpers/strings.helper';
import { TimelineModel } from '@mpt-fo/common/shared/models/timeline.model';
import { AuthenticationService } from '@mpt-fo/common/core/services/authentication.service';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MessageModalComponent } from '../../../login/partials/message-modal/message-modal.component';
import { ModalOptionsConfig, ModalService } from '@mpt-fo/common/lean-shared/components/nz-modal-service/modal.service';
import { DEFAULT_MODAL_KAMONA_CLASSES, DEFAULT_MODAL_WIDTH, MONIEPOINT_DESIGN_TYPE } from '@mpt-fo/common/shared/constants';
import { CountdownType } from '@mpt-fo/common/kamona/components/otp-count-down/otp-count-down.component';
import { Router } from '@angular/router';
import { IdentityProvider } from '@mpt-fo/common/kamona/model/kamonal-model';
import { AuthenticationEvents, DataLayerService } from '@mpt-fo/common/core/services/data-layer-service.service';
import { LocationHelper } from '@mpt-fo/common/core/helpers/location.helper';

@Component({
	selector: 'app-password-otp-validation',
	templateUrl: './password-otp-validation.component.html',
	styleUrls: ['./password-otp-validation.component.scss'],
})
export class PasswordOtpValidationComponent implements OnInit {
	@Input() step: TimelineModel;
	@Input() stepsArray: TimelineModel[];
	@Output() back: EventEmitter<any> = new EventEmitter();
	@Output() continue: EventEmitter<any> = new EventEmitter();
	@Input() useOtp = false;
	@Input() livelinessCheckReference = '';
	@Input() identityProvider = IdentityProvider.METAMAP;
	readonly DEFAULT_ERROR_MESSAGE = 'An Error occurred';
	runningModal: any;
	otpErrorMessage: string;
	otp: any;
	readonly PIN_LENGTH = 6;
	disableOtpButton: boolean;
	submittingForm: boolean;
	otpSuspensionReleaseTime: any;
	loading: boolean;
	ussdCode: any;
	readonly CountdownType = CountdownType;
	readonly feature = 'PASSWORD_RESET';

	constructor(
		private auth: AuthenticationService,
		private modalService: ModalService,
		private router: Router,
		private dataLayerService: DataLayerService,
		private locationHelper: LocationHelper,
	) {}

	ngOnInit(): void {
		this.otp = null;
		this.ussdCode = this.stepsArray[0]?.response?.ussdCode;
		this.dataLayerService.logEventWithData(AuthenticationEvents.initiated, this.getEventData());
	}

	async getEventData(errorDetails?: { errorCode?: string; errorMessage: string; }) {
		const geoHeaders = await this.locationHelper.getCoordinates();

		return {
			auth_type: this.useOtp ? 'OTP' : 'FACE_MATCH_CHECK',
			auth_type_provider: this.useOtp ? '' : this.identityProvider,
			feature: this.feature,
			...(geoHeaders ? {
				longitude: geoHeaders.longitude,
				latitude: geoHeaders.latitude,
			} : {}),
			...(errorDetails ? {
				api_error_code: errorDetails.errorCode,
				api_error_message: errorDetails.errorMessage,
				error_message: errorDetails.errorMessage,
			} : {}),
		};
	}

	setPin($event: string) {
		this.otp = $event;
		this.disableOtpButton = $event.length === this.PIN_LENGTH;
	}

	validateOTP() {
		const body = {
			username: this.stepsArray[0]?.data?.username,
			otp: this.otp,
		};
		this.submittingForm = true;
		this.auth
			.validatePasswordOTP(body)
			.then((res) => {
				this.step.response = res;
				this.dataLayerService.logEventWithData(AuthenticationEvents.success, this.getEventData());
				this.continue.emit(this.otp);
				this.submittingForm = false;
			})
			.catch((err) => {
				const description = StringHelper.getErrorMessageHttpErrorResponse(err) || this.DEFAULT_ERROR_MESSAGE;
				this.dataLayerService.logEventWithData(AuthenticationEvents.failed, this.getEventData({
					errorCode: err?.error?.code,
					errorMessage: description,
				}));
				this.launchAlertModal({ description, title: 'Login Failed' });
				this.submittingForm = false;
			});
	}

	launchAlertModal(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.alertType = 'error';
		config.nzComponentParams.title = params?.title;
		config.className = DEFAULT_MODAL_KAMONA_CLASSES;
		config.nzWidth = DEFAULT_MODAL_WIDTH;
		config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
		config.nzComponentParams.callbackName = params?.callbackName || 'Close';
		config.nzComponentParams.message = params?.description;
		this.runningModal = this.modalService.alert(config);
	}

	launchMessageModal(params) {
		const config = this.createModalConfig(params);
		this.runningModal = this.modalService.createModal(MessageModalComponent, config);
	}

	createModalConfig(params) {
		const config = new ModalOptionsConfig();
		config.nzComponentParams.data = params;
		return config;
	}

	reGeneratePasswordOTP() {
		this.loading = true;
		this.otpErrorMessage = null;
		this.otpSuspensionReleaseTime = null;
		const username = { username: this.stepsArray[0]?.data?.username };
		this.auth
			.generatePasswordOTP(username)
			.then((res) => {
				this.stepsArray[0].response = res;
				this.ussdCode = res?.ussdCode;
				this.loading = false;
			})
			.catch((err) => {
				this.otpErrorMessage = StringHelper.getErrorMessageHttpErrorResponse(err);
				this.otpSuspensionReleaseTime = err?.error?.suspensionReleaseTime;
				this.loading = false;
			});
	}

	next() {
		if (!this.disableOtpButton) {
			return;
		}
		this.step.data = this.otp;
		this.validateOTP();
	}

	getUsername() {
		return this.stepsArray?.[0]?.data?.username;
	}

	navigateToChangePassword(username, validationKey) {
		this.router.navigate([`/recover-password`], {
			queryParams: {
				token: validationKey,
				username: username,
			},
		})
		.then(() => {
			this.submittingForm = false;
		});
	}

	livelinessStepCompleted($evt) {
		if ($evt === -1) {
			this.back.emit();
			return;
		}
		this.navigateToChangePassword(
			this.stepsArray[0]?.data?.username,
			this.livelinessCheckReference,
		);
	}
}
